/**
 * Hoy — la mañana del curador (hoy.html): cola con SLA, drafts a medias,
 * casas que piden ojo, el pulso del asesor. Estados: cargando (skeleton),
 * error de red (banner + datos marcados como desactualizados), día.
 * Real donde la API llega (skills, implantaciones); el resto, seed.ts.
 */
import { Link } from "@tanstack/react-router";
import { useMsal } from "@azure/msal-react";
import { Badge, Banner, Button, Card, CardHeader, CardPad, Fainted, Feed, FeedItem, Mono, PageHead, Skeleton, Stale, StatCell, StatGrid, buttonClass } from "@skein/ui";
import { useEngagements, useMe, useSkills } from "../../api/queries";
import { HOY_SEED as S } from "./seed";

const fecha = new Intl.DateTimeFormat("es-ES", { weekday: "long", day: "numeric", month: "long" });

function Cargando() {
  const line = (w: string, extra?: React.CSSProperties) => <Skeleton style={{ height: ".9em", width: w, ...extra }} />;
  return (
    <section aria-busy="true" aria-label="Cargando el día">
      <PageHead title="" skeleton />
      <div className="manana">
        <Card>{line("50%", { marginBottom: "var(--sk-sp-3)" })}<Skeleton style={{ height: "2.5rem" }} /></Card>
        <Card>{line("60%", { marginBottom: "var(--sk-sp-3)" })}{line("100%")}{line("75%", { marginTop: "var(--sk-sp-2)" })}</Card>
        <Card>{line("55%", { marginBottom: "var(--sk-sp-3)" })}{line("100%")}{line("65%", { marginTop: "var(--sk-sp-2)" })}</Card>
        <Card>{line("45%", { marginBottom: "var(--sk-sp-3)" })}<Skeleton style={{ height: "2.5rem" }} /></Card>
      </div>
    </section>
  );
}

export function HoyPage() {
  const { accounts } = useMsal();
  const me = useMe();
  const skills = useSkills();
  const engagements = useEngagements();
  const nombre = (me.data?.principal?.name || accounts[0]?.name || "").split(" ")[0];
  if (skills.isPending || engagements.isPending) return <Cargando />;
  const error = skills.isError || engagements.isError;
  // en error sin datos previos no se imprime 0 como si fuera dato (el prototipo conserva el último número)
  const nDrafts = skills.data ? skills.data.filter((s) => s.estado === "draft").length : undefined;
  const nCasas = engagements.data?.length;
  return (
    <>
      {error && (
        <Banner tone="danger" style={{ marginBottom: "var(--sk-gap)" }}>
          <b>No llego al Studio.</b> Los números que ves pueden estar desactualizados. Reintento solo cada 30 s.{" "}
          <Button variant="link" onClick={() => { void skills.refetch(); void engagements.refetch(); }}>reintentar ahora</Button>
        </Banner>
      )}
      <Stale stale={error}>
        <PageHead
          title={nombre ? `Buenos días, ${nombre}` : "Buenos días"}
          meta={<>
            <Badge>{fecha.format(new Date())}</Badge>
            {S.slaVencidos > 0 && <Badge tone="danger">{S.slaVencidos} SLA vencido</Badge>}
            <Fainted style={{ fontSize: "var(--sk-fs-2xs)", alignSelf: "center" }}>{S.actosRegistro} actos en el registro desde las {S.desde}</Fainted>
          </>}
        />
        <div className="manana">
          <Card flush aria-label="La cola te espera">
            <CardHeader title="La cola te espera" hint="nada llega a producción sin pasar por ella" />
            <StatGrid>
              <StatCell n={S.cola.pendientes} label="pendientes" />
              <StatCell n={S.cola.slaVencido} label={S.cola.slaDetalle} danger />
              <StatCell n={S.cola.esperaMedia} label="media de espera hoy" />
            </StatGrid>
            <CardPad><Link to="/cola" className={buttonClass({ size: "s", variant: "go" })}>Ir a la cola</Link></CardPad>
          </Card>

          <Card flush aria-label="Lo que quedó a medias">
            <CardHeader title="Lo que quedó a medias" hint={nDrafts === undefined ? "drafts sin publicar" : `${nDrafts} drafts sin publicar`} />
            <Feed full>
              <FeedItem t={S.aMedias.hoy.t}><Link to="/skills/$slug" params={{ slug: S.aMedias.hoy.slug }} className={buttonClass({ variant: "link" }, "sk-mono")}>{S.aMedias.hoy.slug}</Link> · <b>{S.aMedias.hoy.version}</b> {S.aMedias.hoy.texto}</FeedItem>
              <FeedItem t="ayer"><Fainted><Mono>{S.aMedias.ayer.drafts[0]}</Mono></Fainted> y <Fainted><Mono>{S.aMedias.ayer.drafts[1]}</Mono></Fainted> siguen <b>draft — sin publicar</b> · <Fainted><Mono>{S.aMedias.ayer.menor}</Mono></Fainted> arrastra un draft menor</FeedItem>
              <FeedItem t="biblioteca"><Link to="/conocimiento" className={buttonClass({ variant: "link" })}>{S.aMedias.biblioteca.borradoresSinFuente} borradores</Link> de conocimiento <b>pendientes de fuente citable</b></FeedItem>
            </Feed>
          </Card>

          <Card flush aria-label="Casas que piden ojo">
            <CardHeader title="Casas que piden ojo" hint={nCasas === undefined ? "de todas" : `de las ${nCasas}`} />
            <Feed full>
              {S.casas.map((h) => (
                <FeedItem key={h.nombre} t={<Badge tone={h.estado === "parada" ? "warn" : "accent"}>{h.estado}</Badge>}><b>{h.nombre}</b> — {h.detalle}</FeedItem>
              ))}
            </Feed>
            <CardPad><Link to="/implantaciones" className={buttonClass({ size: "s", variant: "quiet" })}>Todas las casas</Link></CardPad>
          </Card>

          <Card flush aria-label="El pulso del asesor">
            <CardHeader title="El pulso del asesor" hint={`hoy, desde las ${S.desde}`} />
            <StatGrid>
              <StatCell n={S.pulso.actos} label="actos en el registro" />
              <StatCell n={S.pulso.respuestasConTraza} label="respuestas con traza" />
              <StatCell n={S.pulso.toquesMemoria} label="toques de memoria" />
            </StatGrid>
            <CardPad><Link to="/auditoria" className={buttonClass({ size: "s", variant: "quiet" })}>Abrir la auditoría</Link></CardPad>
          </Card>
        </div>
      </Stale>
    </>
  );
}
